import axios from './common';

if (process.env.VUE_APP_FAKE_SERVICES === 'true') {
    require('./fake/images.js');
}

const state = {
    images: [],
    imagesQueryParams: {
        description: null,
        limit: 10,
        page: 1,
    },
    currentImageItem: null,
    editableImageItem: null,
    imagesLoading: false,
    imageItemLoading: false,
    imageApiError: null,
    totalImages: 0,
};

const getters = {
    images: state => {
        return state.images;
    },
    imagesQueryParams: state => {
        return state.imagesQueryParams;
    },
    imagesLoading: state => {
        return state.imagesLoading;
    },
    currentImageItem: state => {
        return state.currentImageItem;
    },
    editableImageItem: state => {
        return state.editableImageItem;
    },
    imageItemLoading: state => {
        return state.imageItemLoading;
    },
    imageApiError: state => {
        return state.imageApiError;
    },
    totalImages: state => {
        return state.totalImages;
    },
};

const actions = {
    getImages(context) {

        context.dispatch('addNotification', {
            status: 'loading',
            message: 'Loading Images',
        }, {root: true}).then(() => context.commit('setImagesLoading', true));

        axios.get(`${process.env.VUE_APP_SERVICE_URI_FILE}/api/images/`, {
            params: context.getters.imagesQueryParams,
        })
            .then(response => {
                context.dispatch('addNotification', {
                    status: 'success',
                    message: 'Images successfully loaded',
                }, {root: true}).then(function() {
                    context.commit('setImagesLoading', false);
                    context.commit('setImages', response.data);
                });
            });
    },
    async deleteImage(context, item) {

        context.dispatch('addNotification', {
            status: 'loading',
            message: `Deleting Image "${item.originalName}"`,
        }, {root: true}).then(() => context.commit('setImageItemLoading', true));

        try {
            await axios.delete(`${process.env.VUE_APP_SERVICE_URI_FILE}/api/images/${item._id}`);
            await context.dispatch('addNotification', {
                status: 'success',
                message: `Successfully deleted Image "${item.originalName}"`,
            }, {root: true});
            return true;
        } catch(error) {
            if (error.response && error.response.status === 404) {
                await context.dispatch('addNotification', {
                    status: 'error',
                    message: `Image "${item.originalName}" was deleted and no longer exists`,
                }, {root: true});
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Delete failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    // client received an error response (5xx, 4xx) or no response at all
                    context.commit('setImageApiError', error);
                    console.error('imageApiError', error);
                });
            }
            return false;
        } finally {
            context.commit('setImageItemLoading', false);
            await context.dispatch('clearImageItems');
            await context.dispatch('getImages');
        }
    },
    async getImage(context, item) {
        context.dispatch('addNotification', {
            status: 'loading',
            message: `Retrieving Image "${item.originalName}"`,
        }, {root: true}).then(function () {
            context.commit('setImageItemLoading', true);
            context.commit('setImageApiError', null);
        });

        try {
            const response = await axios.get(`${process.env.VUE_APP_SERVICE_URI_FILE}/api/images/${item._id}`);
            context.commit('setCurrentImageItem', response.data);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                await context.dispatch('addNotification', {
                    status: 'error',
                    message: `Image "${item.originalName}" deleted and no longer exists`,
                }, {root: true});
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Retrieval failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    // client received an error response (5xx, 4xx) or no response at all
                    console.error('imageApiError', error);
                });
            }
        } finally {
            context.commit('setImageItemLoading', false);
        }
    },
    async editImage(context, item) {

        context.dispatch('addNotification', {
            status: 'loading',
            message: `Editing Image "${item.originalName}"`,
        }, {root: true}).then(function() {
            context.commit('setImageItemLoading', true);
            context.commit('setImageApiError', null);
        });

        try {
            await axios.put(`${process.env.VUE_APP_SERVICE_URI_FILE}/api/images/${item._id}`, item);

            await context.dispatch('addNotification', {
                status: 'success',
                message: `Image "${item.originalName}" successfully edited `,
            }, {root: true});

            return true;

        } catch (error) {
            if (error.response && error.response.status === 404) {
                await context.dispatch('addNotification', {
                    status: 'error',
                    message: `Image "${item.originalName}" was deleted and no longer exists`,
                }, {root: true});
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Edit failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    // client received an error response (5xx, 4xx) or no response at all
                    context.commit('imageApiError', error);
                    console.error('imageApiError', error);
                });
            }

            return false;
        } finally {
            context.commit('setImageItemLoading', false);
            await context.dispatch('clearImageItems');
            await context.dispatch('getImages');
        }
    },
    async addImage(context, item) {

        if(! item) {

            context.dispatch('addNotification', {
                status: 'error',
                message: `Failed to add Image, please try again`,
            }, {root: true}).then(() => {
                context.commit('setImageApiError', {
                    fields: {
                        image: 'Please select',
                    },
                });
            });

            return;
        }

        context.dispatch('addNotification', {
            status: 'loading',
            message: `Adding Image "${item.name}"`,
        }, {root: true}).then(function () {
            context.commit('setImageItemLoading', true);
            context.commit('setImageApiError', null);
        });

        try {
            const formData = new FormData();
            formData.append('file', item);
            await axios.post(`${process.env.VUE_APP_SERVICE_URI_FILE}/api/images/`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            await context.dispatch('addNotification', {
                status: 'success',
                message: `Image "${item.name}" successfully added`,
            }, {root: true});

            return true;

        } catch (error) {
            if (error.response) {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Failed to add Image "${item.originalName}", please try again`,
                }, {root: true}).then(() => {
                    context.commit('setImageApiError', error.response.data);
                });
            } else {
                context.dispatch('addNotification', {
                    status: 'error',
                    message: `Add failed. An unknown error occurred`,
                }, {root: true}).then(() => {
                    context.commit('setImageApiError', error);
                    console.error('imageApiError', error);
                });
            }

            return false;
        } finally {
            context.commit('setImageItemLoading', false);
            await context.dispatch('getImages');
        }
    },
    clearImageItems(context) {
        context.commit('setCurrentImageItem', null);
        context.commit('setEditableImageItem', null);
    },
    clearImageApiError(context) {
        context.commit('setImageApiError', null);
    },
    setImagesPaginationOptions(context, options) {
        context.commit('imagesPaginationOptions', options);
    },
    setImagesSearch(context, search) {
        context.commit('imagesSearch', search);
    },
};

const mutations = {
    imagesPaginationOptions(state, options) {
        state.imagesQueryParams.limit = options.itemsPerPage ?? 10;
        state.imagesQueryParams.page = options.page ?? 1;
    },
    imagesSearch(state, search) {
        // check to ensure search is a string, you cannot trim a null
        if (typeof search === 'string') {
            state.imagesQueryParams.alt = `/${search.trim()}/i`;
        }
    },
    setImages(state, responseData) {
        state.images = responseData.docs;
        state.totalImages = responseData.totalDocs;
    },
    setCurrentImageItem(state, item) {
        state.currentImageItem = item;
        state.editableImageItem = {...item};
    },
    setEditableImageItem(state, item) {
        state.editableImageItem = item;
    },
    setImageItemLoading(state, isLoading) {
        state.imageItemLoading = isLoading;
    },
    setImagesLoading(state, isLoading) {
        state.imagesLoading = isLoading;
    },
    setImageApiError(state, error) {
        state.imageApiError = error;
    },
};

export default {
    state,
    getters,
    actions,
    mutations,
};
